import { useEffect, useMemo, useRef, useState } from 'react'
import type { CharacterId } from '../types'

const CDN_BASE = 'https://d2ql3aoebfyj3b.cloudfront.net/game/Quegudan2'

/** 캐릭터별 동적 이미지 매니페스트 생성 */
function buildManifest(character: CharacterId) {
  return {
    // 캐릭터별 유닛
    unitOff: `${CDN_BASE}/image/mainGame/${character}/${character}_off.png`,
    unitOn: `${CDN_BASE}/image/mainGame/${character}/${character}_on.png`,
    unitSelect: `${CDN_BASE}/image/mainGame/${character}/${character}_select.png`,
    unitDone: `${CDN_BASE}/image/mainGame/${character}/${character}_done.png`,

    // 캐릭터별 패널 (체커보드)
    pannel1: `${CDN_BASE}/image/mainGame/${character}/${character}_pannel1.png`,
    pannel2: `${CDN_BASE}/image/mainGame/${character}/${character}_pannel2.png`,

    // 표시등 (공통)
    sideOff: `${CDN_BASE}/image/mainGame/side_off.png`,
    sideFull: `${CDN_BASE}/image/mainGame/side_full.png`,

    // 정답 숫자 이미지 (0~9 on/off)
    num0Off: `${CDN_BASE}/image/mainGame/0_off.png`,
    num1Off: `${CDN_BASE}/image/mainGame/1_off.png`,
    num2Off: `${CDN_BASE}/image/mainGame/2_off.png`,
    num3Off: `${CDN_BASE}/image/mainGame/3_off.png`,
    num4Off: `${CDN_BASE}/image/mainGame/4_off.png`,
    num5Off: `${CDN_BASE}/image/mainGame/5_off.png`,
    num6Off: `${CDN_BASE}/image/mainGame/6_off.png`,
    num7Off: `${CDN_BASE}/image/mainGame/7_off.png`,
    num8Off: `${CDN_BASE}/image/mainGame/8_off.png`,
    num9Off: `${CDN_BASE}/image/mainGame/9_off.png`,
    num0On: `${CDN_BASE}/image/mainGame/0_on.png`,
    num1On: `${CDN_BASE}/image/mainGame/1_on.png`,
    num2On: `${CDN_BASE}/image/mainGame/2_on.png`,
    num3On: `${CDN_BASE}/image/mainGame/3_on.png`,
    num4On: `${CDN_BASE}/image/mainGame/4_on.png`,
    num5On: `${CDN_BASE}/image/mainGame/5_on.png`,
    num6On: `${CDN_BASE}/image/mainGame/6_on.png`,
    num7On: `${CDN_BASE}/image/mainGame/7_on.png`,
    num8On: `${CDN_BASE}/image/mainGame/8_on.png`,
    num9On: `${CDN_BASE}/image/mainGame/9_on.png`,

    // 정답 애니메이션 스프라이트 (공통)
    rightAnim: `${CDN_BASE}/image/animation/right.png`,
  } as const
}

type Manifest = ReturnType<typeof buildManifest>
export type ImageKey = keyof Manifest
export type ImageMap = Record<ImageKey, HTMLImageElement | null>

function createEmptyMap(manifest: Manifest): ImageMap {
  const init = {} as ImageMap
  for (const key of Object.keys(manifest) as ImageKey[]) {
    init[key] = null
  }
  return init
}

// 전역 캐시: 캐릭터별로 로드된 이미지를 보관 (리마운트 시 즉시 사용)
const imageCache = new Map<string, { images: ImageMap; loaded: boolean }>()

export function useImageLoader(character: CharacterId = 'ddami') {
  const manifest = useMemo(() => buildManifest(character), [character])
  const cached = imageCache.get(character)

  const [images, setImages] = useState<ImageMap>(() =>
    cached?.loaded ? cached.images : createEmptyMap(manifest)
  )
  const [loaded, setLoaded] = useState(() => cached?.loaded ?? false)
  const loadGenRef = useRef(0)

  useEffect(() => {
    // 캐시에 이미 로드된 이미지가 있으면 즉시 반영
    const existing = imageCache.get(character)
    if (existing?.loaded) {
      setImages(existing.images)
      setLoaded(true)
      return
    }

    const gen = ++loadGenRef.current
    setLoaded(false)

    const currentManifest = buildManifest(character)
    const entries = Object.entries(currentManifest) as [ImageKey, string][]
    let loadCount = 0
    const total = entries.length
    const result = createEmptyMap(currentManifest)

    entries.forEach(([key, url]) => {
      const img = new window.Image()
      img.crossOrigin = 'anonymous'
      const onDone = () => {
        if (gen !== loadGenRef.current) return // stale load
        loadCount++
        if (loadCount === total) {
          const finalImages = { ...result }
          imageCache.set(character, { images: finalImages, loaded: true })
          setImages(finalImages)
          setLoaded(true)
        }
      }
      img.onload = () => {
        result[key] = img
        onDone()
      }
      img.onerror = () => {
        result[key] = null
        onDone()
      }
      img.src = url
    })
  }, [character])

  return { images, loaded }
}
